/**
 * Nether natural spawning: vanilla per-biome monster tables (only the mobs
 * implemented so far), any light level, on solid non-lava floor with room
 * above. Runs instead of the overworld Spawner while the player is in the
 * Nether (main.ts disables EntityManager.spawningEnabled there).
 */

import { IS_SOLID, LAVA_ID, NETHER_WART_BLOCK_ID, WARPED_WART_BLOCK_ID, BEDROCK_ID } from '../../blocks';
import type { MobType } from '../../game/types';
import type { World } from '../../world/chunk';
import { ChunkState } from '../../world/chunk';
import { NetherBiome } from '../../worldgen/nether';

interface Entry { type: MobType; weight: number; min: number; max: number }

/** Vanilla weights / group sizes (striders are added on lava separately). */
/** Vanilla nether fortress structure spawns (used inside fortress bounds). */
export const FORTRESS_SPAWNS: Entry[] = [
  { type: 'blaze', weight: 10, min: 2, max: 3 },
  { type: 'zombified_piglin', weight: 5, min: 4, max: 4 },
  { type: 'wither_skeleton', weight: 8, min: 5, max: 5 },
  { type: 'skeleton', weight: 2, min: 5, max: 5 },
  { type: 'magma_cube', weight: 3, min: 4, max: 4 },
];

/** Bastion remnants: piglins in groups, now and then a lone brute. */
export const BASTION_SPAWNS: Entry[] = [
  { type: 'piglin', weight: 10, min: 2, max: 4 }, { type: 'piglin_brute', weight: 2, min: 1, max: 1 },
];

export const NETHER_SPAWNS: Record<NetherBiome, Entry[]> = {
  [NetherBiome.Wastes]: [
    { type: 'zombified_piglin', weight: 100, min: 4, max: 4 },
    { type: 'ghast', weight: 50, min: 4, max: 4 },
    { type: 'magma_cube', weight: 2, min: 4, max: 4 },
    { type: 'enderman', weight: 1, min: 4, max: 4 },
    { type: 'piglin', weight: 15, min: 4, max: 4 },
  ],
  [NetherBiome.SoulSandValley]: [
    { type: 'skeleton', weight: 20, min: 5, max: 5 },
    { type: 'ghast', weight: 50, min: 4, max: 4 },
    { type: 'enderman', weight: 1, min: 4, max: 4 },
  ],
  [NetherBiome.CrimsonForest]: [
    { type: 'zombified_piglin', weight: 1, min: 2, max: 4 },
    { type: 'hoglin', weight: 9, min: 3, max: 4 },
    { type: 'piglin', weight: 5, min: 3, max: 4 },
  ],
  [NetherBiome.WarpedForest]: [{ type: 'enderman', weight: 1, min: 4, max: 4 }],
  [NetherBiome.BasaltDeltas]: [
    { type: 'ghast', weight: 40, min: 1, max: 1 },
    { type: 'magma_cube', weight: 100, min: 2, max: 5 },
  ],
};

/** Monster cap near the player (vanilla 70 per 17x17 chunks, scaled to our view). */
export const NETHER_CAP = 24;

export interface NetherSpawnHost {
  world: World;
  rnd(): number;
  player(): { x: number; y: number; z: number };
  biomeAt(x: number, z: number): NetherBiome;
  spawn(type: MobType, x: number, y: number, z: number): unknown;
  /** Living hostile mobs currently loaded. */
  count(): number;
  /** Fortress bounds query (worldgen/netherFortress.ts inFortress). */
  inFortress?(x: number, y: number, z: number): boolean;
  /** Bastion remnant bounds query (worldgen/netherStructures.ts inBastion): piglin country. */
  inBastion?(x: number, y: number, z: number): boolean;
  /** Loaded mobs of one type (strider cap). */
  countType?(t: MobType): number;
}

export class NetherSpawner {
  private timer = 0;
  constructor(private readonly host: NetherSpawnHost) {}

  tick(): void {
    if (++this.timer < 10) return;
    this.timer = 0;
    const h = this.host;
    let have = h.count();
    if (have >= NETHER_CAP) return;
    const p = h.player();
    const rnd = h.rnd;
    const ang = rnd() * Math.PI * 2;
    const dist = 24 + rnd() * 40;
    const x = Math.floor(p.x + Math.cos(ang) * dist);
    const z = Math.floor(p.z + Math.sin(ang) * dist);
    const fortress = h.inFortress?.(x, Math.floor(p.y), z) ?? false;
    const bastion = !fortress && (h.inBastion?.(x, Math.floor(p.y), z) ?? false);
    const table = fortress ? FORTRESS_SPAWNS : bastion ? BASTION_SPAWNS : NETHER_SPAWNS[h.biomeAt(x, z)];
    if (!table.length) return;
    let total = 0;
    for (const e of table) total += e.weight;
    let r = rnd() * total;
    let pick = table[0];
    for (const e of table) { if ((r -= e.weight) < 0) { pick = e; break; } }
    // striders: on any lava surface (vanilla weight 60 in every biome's creature list)
    if (rnd() < 0.15) { this.strider(x, z); return; }
    const y0 = this.floor(x, Math.floor(p.y + (rnd() * 2 - 1) * 24), z);
    if (y0 < 0) return;
    if (pick.type === 'ghast') {
      // needs room: a clear column of air 6 tall at the centre and corners
      for (let yy = y0; yy < y0 + 6; yy++) for (const [ox, oz] of [[-2, -2], [2, 2], [0, 0]]) {
        if (h.world.getBlock(x + ox, yy, z + oz) !== 0) return;
      }
      h.spawn('ghast', x + 0.5, y0 + 1, z + 0.5);
      return;
    }
    const n = pick.min + Math.floor(rnd() * (pick.max - pick.min + 1));
    for (let i = 0; i < n && have < NETHER_CAP; i++) {
      const sx = i === 0 ? x : x + Math.floor(rnd() * 7) - 3;
      const sz = i === 0 ? z : z + Math.floor(rnd() * 7) - 3;
      const sy = i === 0 ? y0 : this.floor(sx, y0 + 2, sz);
      if (sy < 0 || Math.abs(sy - y0) > 3) continue;
      const dx = sx - p.x, dz = sz - p.z;
      if (dx * dx + dz * dz < 24 * 24) continue;
      h.spawn(pick.type, sx + 0.5, sy, sz + 0.5);
      have++;
    }
  }

  private strider(x: number, z: number): void {
    const w = this.host.world;
    const c = w.getChunk(x >> 4, z >> 4);
    if (!c || c.state !== ChunkState.Ready) return;
    if ((this.host.countType?.('strider') ?? 0) >= 6) return;
    for (let y = 40; y > 20; y--) {
      if (w.getBlock(x, y - 1, z) === LAVA_ID && w.getBlock(x, y, z) === 0 && w.getBlock(x, y + 1, z) === 0) {
        this.host.spawn('strider', x + 0.5, y, z + 0.5);
        return;
      }
    }
  }

  /** Feet y of a free 2-tall spot on solid floor near y (scanning down), or -1. */
  private floor(x: number, y: number, z: number): number {
    const w = this.host.world;
    const c = w.getChunk(x >> 4, z >> 4);
    if (!c || c.state !== ChunkState.Ready) return -1;
    for (let yy = Math.min(y, 124); yy >= Math.max(y - 24, 2); yy--) {
      const below = w.getBlock(x, yy - 1, z);
      if (IS_SOLID[below] !== 1 || below === LAVA_ID || below === BEDROCK_ID) continue;
      if (below === NETHER_WART_BLOCK_ID || below === WARPED_WART_BLOCK_ID) continue; // vanilla: not on fungus caps
      if (IS_SOLID[w.getBlock(x, yy, z)] || IS_SOLID[w.getBlock(x, yy + 1, z)]) continue;
      if (w.getBlock(x, yy, z) === LAVA_ID) continue;
      return yy;
    }
    return -1;
  }
}
